import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Satellite, Map as MapIcon, Activity, ArrowRight, LogOut, Shield, KeyRound, FolderOpen, FlaskConical, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BrandLockup } from "@/components/BrandLockup";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { StorageMeter } from "@/components/StorageMeter";
import GeoPiButton from "@/components/GeoPiButton";

const MODES = [
  { id: "01", key: "remote-sensing", to: "/remote-sensing", icon: Satellite,
    title: "REMOTE SENSING", sub: "Satellite-Based Mineral Exploration & Targeting",
    desc: "Commodity-specific spectral analysis, alteration & structural interpretation and ranked exploration targets." },
  { id: "02", key: "maps", to: "/maps", icon: MapIcon,
    title: "GEOMAPPING", sub: "Terrain & GIS Mapping",
    desc: "Location, satellite, DEM, hillshade, slope, aspect, contour, drainage & topography map sheets." },
  { id: "03", key: "geophysics", to: "/geophysics", icon: Activity,
    title: "GEOPHYSICS", sub: "Geophysical Data Processing, Analysis & Interpretation",
    desc: "Upload magnetic, gravity, IP/resistivity, EM or radiometric data for gridding, enhancement & interpretation." },
];

function ClientHome({ user, logout }) {
  const [projects, setProjects] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])); }, []);
  const openPath = (p) => (p.mode === "maps" ? `/maps/${p.id}` : p.mode === "geophysics" ? `/geophysics/${p.id}` : `/projects/${p.id}`);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-home">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-8">
        <BrandLockup />
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500 hidden sm:block">{user?.name || user?.email}</span>
          <ChangePasswordDialog trigger={
            <button data-testid="client-change-password" className="text-slate-400 hover:text-[#C20511]" title="Change password"><KeyRound size={16} /></button>
          } />
          <button onClick={logout} data-testid="client-logout" className="text-slate-400 hover:text-slate-700"><LogOut size={16} /></button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="mb-2 text-[12px] font-semibold tracking-[0.2em] text-[#C20511] uppercase">Client Portal</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">Welcome{user?.name ? `, ${user.name}` : ""}</h1>
        <p className="text-slate-500 max-w-2xl mb-10">Your exploration projects and shared reports, prepared for you by our team.</p>
        {projects === null ? (
          <div className="text-slate-400 text-sm">Loading your projects…</div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="client-empty">
            No projects have been shared with you yet. Please contact your project manager.
          </div>
        ) : (
          <div className="space-y-3" data-testid="client-project-list">
            {projects.map((p) => (
              <button key={p.id} data-testid={`client-project-${p.id}`} onClick={() => nav(openPath(p))}
                className="w-full text-left bg-white border border-slate-200 rounded-xl px-5 py-4 hover:border-[#C20511]/40 hover:shadow-md transition-all flex items-center gap-4 group">
                <div className="h-10 w-10 rounded-lg bg-slate-900 grid place-items-center group-hover:bg-[#C20511] transition-colors shrink-0">
                  <FolderOpen size={18} className="text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{[p.commodity, p.country].filter(Boolean).join(" · ") || "Exploration project"}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 group-hover:text-[#C20511] shrink-0">
                  Open <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const isSuper = user?.role === "super_admin";
  const allowedKeys = isSuper ? MODES.map((m) => m.key) : (user?.active_modules || []);
  const visible = MODES.filter((m) => allowedKeys.includes(m.key));
  if (user?.role === "client_viewer") return <ClientHome user={user} logout={logout} />;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-modes">
      <header className="relative h-[68px] bg-white shadow-[0_1px_4px_rgba(15,23,42,0.07)] flex items-center justify-between gap-3 px-4 sm:px-8 sticky top-0 z-[900]" data-testid="console-header">
        <BrandLockup />
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <GeoPiButton className="hidden md:inline-flex" />
          {["admin", "super_admin"].includes(user?.role) && (
            <button onClick={() => nav("/admin")} data-testid="home-admin" title="Admin control & app settings"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#C20511] transition-colors">
              <Shield size={16} /> <span>Admin</span>
            </button>
          )}
          <div className="hidden lg:block text-[11px] text-slate-400 font-mono"><StorageMeter /></div>
          <div className="flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 pl-2.5 pr-3 py-1.5" data-testid="console-user-pill">
            <Shield size={16} className="text-[#C20511] shrink-0" fill="currentColor" />
            <div className="leading-tight">
              <div className="text-[13px] font-semibold text-slate-900 whitespace-nowrap max-w-[130px] truncate">{user?.name || user?.email}</div>
              <div className="hidden sm:block text-[10px] text-slate-500 -mt-0.5">{user?.role_label}</div>
            </div>
          </div>
          <ChangePasswordDialog trigger={
            <button data-testid="home-change-password" title="Change password"
              className="h-9 w-9 rounded-lg grid place-items-center text-slate-400 hover:text-[#C20511] hover:bg-slate-100 transition-colors"><KeyRound size={18} /></button>
          } />
          <button onClick={logout} data-testid="home-logout" title="Sign out"
            className="h-9 w-9 rounded-lg grid place-items-center text-slate-400 hover:text-[#C20511] hover:bg-slate-100 transition-colors"><LogOut size={18} /></button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#C20511]" />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="mb-2 text-[12px] font-semibold tracking-[0.2em] text-[#C20511] uppercase">Select Work Mode</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">SYMIN</h1>
        <p className="text-slate-500 max-w-2xl mb-12">One platform, three independent workflows. Choose a mode to begin - each has its own inputs, processing and outputs.</p>

        {(!isSuper && user?.role !== "admin" && visible.length === 0) ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center" data-testid="no-modules">
            <div className="h-14 w-14 rounded-2xl bg-slate-900 grid place-items-center mx-auto mb-5">
              <Lock size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">No modules assigned</h2>
            <p className="text-sm text-slate-500 mt-2">Please contact your administrator.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-6">
              {visible.map((m) => (
                <button key={m.id} data-testid={`mode-card-${m.key}`} onClick={() => nav(m.to)}
                  className="group text-left bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#C20511] transition-colors duration-200">
                  <div className="flex items-start justify-between mb-6">
                    <div className="h-12 w-12 rounded-xl bg-slate-900 grid place-items-center shrink-0">
                      <m.icon size={22} className="text-white" />
                    </div>
                    <span className="text-4xl font-bold text-slate-200 leading-none tabular-nums">{m.id}</span>
                  </div>
                  <div className="text-base font-bold text-slate-900 tracking-wide uppercase">{m.title}</div>
                  <div className="text-[13px] text-[#C20511] font-semibold mt-1 mb-3">{m.sub}</div>
                  <p className="text-sm text-slate-500 leading-relaxed mb-6">{m.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 group-hover:text-[#C20511] transition-colors">
                    Open Mode <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </button>
              ))}
            </div>

            <button data-testid="home-explorer" onClick={() => nav("/commodities")}
              className="group mt-6 w-full text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C20511] transition-colors flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center shrink-0">
                <FlaskConical size={20} className="text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-bold text-slate-900">Commodity Model Explorer</div>
                <div className="text-sm text-slate-500">See the diagnostic bands, alteration &amp; structural indicators and palette logic behind every target.</div>
              </div>
              <ArrowRight size={16} className="text-slate-400 group-hover:text-[#C20511] group-hover:translate-x-0.5 transition-all shrink-0" />
            </button>
          </>
        )}

      </main>
    </div>
  );
}
